import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import type { FlowDefinition } from '@shared/schema/flow'
import type { SimulatorSnapshot } from '@shared/engine/simulate'
import { workflowApi } from '../workflows/api'
import { errorMessage } from '../api/client'
import { newId } from '@shared/ids'
import { BlockRenderer } from '../blocks/BlockRenderer'
import { themeStyle } from '../theme/applyTheme'
import { useRunner } from './machine'
import { createQueueSink } from './offline/queueSink'
import {
  cacheDefinition,
  getCachedDefinitionByWorkflow,
  findResumableRun,
  saveRun,
  requestPersistentStorage,
} from './offline/db'
import { startSyncLoop, subscribeSync, retryFailed } from './offline/sync'
import type { SyncState } from './offline/sync'
import { Button, Alert, Dialog, LoadingScreen } from '../ui'

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'resume-prompt'; ready: ReadyData; resume: { submissionId: string; snapshot: SimulatorSnapshot } }
  | { phase: 'ready'; ready: ReadyData }

type ReadyData = {
  definition: FlowDefinition
  workflowId: string
  workflowName: string
  versionId: string
  submissionId: string
  snapshot: SimulatorSnapshot | undefined
  offline: boolean
}

export function RunnerPage() {
  const { id: workflowId = '' } = useParams()
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' })

  useEffect(() => {
    startSyncLoop()
    void requestPersistentStorage()
  }, [])

  useEffect(() => {
    let cancelled = false

    void (async () => {
      let definition: FlowDefinition | null = null
      let versionId = ''
      let workflowName = ''
      let offline = false

      try {
        const response = await workflowApi.runDefinition(workflowId)
        definition = response.definition
        versionId = response.versionId
        const summary = (await workflowApi.runnable()).items.find(
          (item) => item.workflowId === workflowId,
        )
        workflowName = summary?.name ?? 'แบบสอบถาม'
        await cacheDefinition({
          versionId,
          workflowId,
          workflowName,
          versionNumber: response.versionNumber,
          definition,
          cachedAt: Date.now(),
        })
      } catch (err) {
        // เน็ตหลุด → ใช้ definition ที่เคยโหลดไว้ในเครื่อง
        const cached = await getCachedDefinitionByWorkflow(workflowId)
        if (cached) {
          definition = cached.definition
          versionId = cached.versionId
          workflowName = cached.workflowName
          offline = true
        } else {
          if (!cancelled) setLoad({ phase: 'error', message: errorMessage(err) })
          return
        }
      }

      if (cancelled || !definition) return

      const resumable = await findResumableRun(workflowId)
      const base: Omit<ReadyData, 'submissionId' | 'snapshot'> = {
        definition,
        workflowId,
        workflowName,
        versionId,
        offline,
      }

      if (resumable?.snapshot && resumable.versionId === versionId) {
        setLoad({
          phase: 'resume-prompt',
          ready: { ...base, submissionId: newId(), snapshot: undefined },
          resume: { submissionId: resumable.submissionId, snapshot: resumable.snapshot },
        })
      } else {
        setLoad({ phase: 'ready', ready: { ...base, submissionId: newId(), snapshot: undefined } })
      }
    })()

    return () => {
      cancelled = true
    }
  }, [workflowId])

  if (load.phase === 'loading') return <LoadingScreen label="กำลังโหลดแบบสอบถาม..." />

  if (load.phase === 'error') {
    return (
      <div className="mx-auto max-w-md space-y-4 p-6">
        <Alert variant="error">{load.message}</Alert>
        <Link to="/run" className="text-sm text-[var(--fs-primary)] underline">
          กลับไปรายการแบบสอบถาม
        </Link>
      </div>
    )
  }

  if (load.phase === 'resume-prompt') {
    return (
      <div className="mx-auto max-w-md space-y-4 p-6">
        <Alert variant="info">
          พบงานที่ทำค้างไว้ในแบบสอบถามนี้ ต้องการทำต่อจากเดิมหรือเริ่มใหม่?
        </Alert>
        <div className="flex gap-2">
          <Button
            variant="primary"
            onClick={() =>
              setLoad({
                phase: 'ready',
                ready: {
                  ...load.ready,
                  submissionId: load.resume.submissionId,
                  snapshot: load.resume.snapshot,
                },
              })
            }
          >
            ทำต่อจากเดิม
          </Button>
          <Button onClick={() => setLoad({ phase: 'ready', ready: load.ready })}>เริ่มใหม่</Button>
        </div>
      </div>
    )
  }

  return <RunnerSession key={load.ready.submissionId} data={load.ready} />
}

// ---------------------------------------------------------------------------

function RunnerSession({ data }: { data: ReadyData }) {
  const stepKeyRef = useRef('')
  const sink = useMemo(
    () => createQueueSink(data.submissionId, stepKeyRef),
    [data.submissionId],
  )
  const [sync, setSync] = useState<SyncState | null>(null)

  useEffect(() => subscribeSync(setSync), [])

  const startedAtRef = useRef(Date.now())

  useEffect(() => {
    void (async () => {
      await saveRun({
        submissionId: data.submissionId,
        workflowId: data.workflowId,
        versionId: data.versionId,
        workflowName: data.workflowName,
        status: 'in_progress',
        snapshot: data.snapshot ?? null,
        startedAt: startedAtRef.current,
        updatedAt: Date.now(),
      })
      // ยังไม่เคยส่งขึ้นเซิร์ฟเวอร์ → เข้าคิวไว้ (ถ้าเคยส่งแล้ว ฝั่งเซิร์ฟเวอร์รับซ้ำได้อยู่แล้ว)
      await sink.ensureCreated({
        submissionId: data.submissionId,
        workflowId: data.workflowId,
        versionId: data.versionId,
        startedAt: startedAtRef.current,
      })
    })()
  }, [data, sink])

  const runner = useRunner({
    definition: data.definition,
    workflowId: data.workflowId,
    submissionId: data.submissionId,
    restore: data.snapshot,
    onCommitStep: (commit) => {
      void sink.putStep(commit)
    },
    onComplete: (finalSeq, incomplete) => {
      void sink.complete(data.submissionId, finalSeq, incomplete)
    },
    onSnapshot: (snapshot) => {
      void saveRun({
        submissionId: data.submissionId,
        workflowId: data.workflowId,
        versionId: data.versionId,
        workflowName: data.workflowName,
        status: snapshot.finished ? 'completed' : 'in_progress',
        snapshot,
        startedAt: startedAtRef.current,
        updatedAt: Date.now(),
      })
    },
    onLookupMatch: (message) => setLookupNotice(message),
  })

  stepKeyRef.current = runner.state.stepKey

  const [manualEnd, setManualEnd] = useState(false)
  /** ข้อความ popup ตอนเจอว่าเป็นร้านเก่า — null = ไม่มี popup ค้างอยู่ */
  const [lookupNotice, setLookupNotice] = useState<string | null>(null)

  const { state } = runner
  const isEndNode = state.node.type === 'end'
  const theme = data.definition.theme
  /** มีการตั้งสีพื้นหลังเอง (เช่นตัวอย่างธีมแดง) — ต้องห่อเนื้อหาด้วยการ์ดสีขาวให้อ่านออก */
  const colored = theme.backgroundColor !== null

  /** จบการสัมภาษณ์ได้ทันทีไม่ว่าจะอยู่ขั้นตอนไหน — คำถามที่เหลือถือว่าข้าม ไม่บังคับตอบให้ครบ */
  const endInterviewNow = () => {
    if (state.status === 'finished') return
    if (!confirm('ต้องการจบการสัมภาษณ์ตอนนี้เลยหรือไม่? คำถามที่ยังไม่ได้ตอบจะถือว่าข้าม')) return
    setManualEnd(true)
    runner.finish(true)
  }

  return (
    <div
      style={{
        ...themeStyle(theme),
        ...(colored ? { backgroundColor: theme.backgroundColor ?? undefined } : {}),
      }}
      className={`flex min-h-screen flex-col ${colored ? '' : 'bg-white'}`}
    >
      <RunnerHeader
        workflowName={data.workflowName}
        theme={theme}
        colored={colored}
        backgroundColor={theme.backgroundColor}
        progress={state.status === 'finished' ? 1 : state.progress}
      />

      <StatusBanner sync={sync} offlineDefinition={data.offline} />

      {/* เจอว่าเป็นร้านเก่า — เด้ง popup ให้กดรับทราบ แล้วทำต่อได้ตามปกติ (ไม่บล็อกการทำงาน) */}
      <Dialog
        open={lookupNotice !== null}
        title="ข้อมูลจากการสำรวจครั้งก่อน"
        onClose={() => setLookupNotice(null)}
        width="max-w-sm"
        footer={
          <Button variant="primary" onClick={() => setLookupNotice(null)}>
            รับทราบ
          </Button>
        }
      >
        <p className="whitespace-pre-wrap text-slate-700">{lookupNotice}</p>
      </Dialog>

      {/* pb เผื่อพื้นที่ด้านล่างไว้เสมอ กันปุ่ม "ถัดไป"/"ย้อนกลับ" โดนปุ่มลอย "จบการสัมภาษณ์" ทับตอนเลื่อนถึงท้ายคำถาม */}
      <div className="mx-auto w-full max-w-xl flex-1 px-4 pb-24 pt-5">
        <div className={colored ? 'rounded-2xl bg-white p-4 shadow-sm' : ''}>
          {state.status === 'finished' ? (
            <FinishedScreen
              message={
                manualEnd
                  ? 'จบการสัมภาษณ์แล้ว'
                  : state.node.type === 'end'
                    ? state.node.message
                    : 'บันทึกข้อมูลเรียบร้อย'
              }
              pending={sync?.pending ?? 0}
              failed={sync?.failed ?? 0}
            />
          ) : (
            <>
              {state.errorMessage && (
                <div className="mb-4">
                  <Alert variant="error">{state.errorMessage}</Alert>
                </div>
              )}

              <BlockRenderer
                blocks={runner.blocks}
                mode="run"
                values={state.answers}
                errors={state.errors}
                onChange={runner.setValue}
                photo={sink.photo}
              />

              <div className="mt-8 flex gap-3">
                {state.canGoBack && (
                  <Button onClick={runner.back} className="min-w-24">
                    ย้อนกลับ
                  </Button>
                )}
                <Button
                  variant="primary"
                  className="flex-1"
                  disabled={state.status === 'error'}
                  onClick={() => void (isEndNode ? runner.finish(false) : runner.next())}
                >
                  {isEndNode ? 'ส่งข้อมูล' : 'ถัดไป'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ปุ่มจบการสัมภาษณ์ — ลอยอยู่มุมขวาล่างเสมอ ไม่ว่าจะอยู่ขั้นตอนไหน จนกว่าจะจบงาน */}
      {state.status !== 'finished' && (
        <button
          type="button"
          onClick={endInterviewNow}
          className="fixed bottom-4 end-4 z-20 rounded-full bg-black/70 px-4 py-2.5 text-sm font-medium text-white shadow-lg backdrop-blur-sm hover:bg-black/80"
        >
          จบการสัมภาษณ์
        </button>
      )}
    </div>
  )
}

function RunnerHeader({
  workflowName,
  theme,
  colored,
  backgroundColor,
  progress,
}: {
  workflowName: string
  theme: FlowDefinition['theme']
  colored: boolean
  backgroundColor: string | null
  progress: number
}) {
  return (
    <div
      className={colored ? 'sticky top-0 z-10' : 'sticky top-0 z-10 border-b border-slate-200 bg-white'}
      // ★ ธีมกำหนดสีพื้นหลังเอง (colored) ต้องทาสีทึบตรงนี้ด้วย ไม่งั้นหัวข้อ/โลโก้ที่ sticky
      // จะโปร่งใส เห็นเนื้อหาที่เลื่อนขึ้นมาทะลุออกมาซ้อนกับโลโก้ (บั๊กที่ผู้ใช้เจอ)
      style={colored ? { backgroundColor: backgroundColor ?? undefined } : undefined}
    >
      <div
        className={
          colored
            ? 'mx-auto flex w-full max-w-xl flex-col items-center gap-1 px-4 py-5 text-center'
            : 'mx-auto flex w-full max-w-xl items-center gap-3 px-4 py-3'
        }
      >
        {theme.logoAssetId ? (
          <img
            src={`/api/assets/${theme.logoAssetId}`}
            alt=""
            className={colored ? 'h-16 w-auto' : 'h-8 w-auto'}
          />
        ) : (
          <div className={colored ? '' : 'min-w-0'}>
            <p
              className="truncate text-sm font-semibold"
              style={{ color: colored ? 'var(--fs-on-bg)' : 'var(--fs-primary)' }}
            >
              {theme.brandText}
            </p>
            {theme.brandSubtext && (
              <p
                className="truncate text-xs"
                style={colored ? { color: 'var(--fs-on-bg)', opacity: 0.85 } : undefined}
              >
                {theme.brandSubtext}
              </p>
            )}
          </div>
        )}
        {!colored && <p className="ms-auto truncate text-xs text-slate-400">{workflowName}</p>}
      </div>
      <div
        className={colored ? 'h-1.5 w-full bg-black/10' : 'h-1.5 w-full bg-slate-100'}
        role="progressbar"
        aria-valuenow={Math.round(progress * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="ความคืบหน้า"
      >
        <div
          className="h-full bg-[var(--fs-progress)] transition-[width] duration-300"
          style={{ width: `${Math.round(progress * 100)}%` }}
        />
      </div>
    </div>
  )
}

function StatusBanner({ sync, offlineDefinition }: { sync: SyncState | null; offlineDefinition: boolean }) {
  if (!sync) return null

  if (sync.needsLogin) {
    return (
      <div className="bg-red-600 px-4 py-2 text-center text-sm text-white">
        เซสชันหมดอายุ — <a href="/login" className="underline">เข้าสู่ระบบใหม่</a>{' '}
        แล้วข้อมูลที่ค้างอยู่จะถูกส่งให้อัตโนมัติ
      </div>
    )
  }

  if (!sync.online || offlineDefinition) {
    return (
      <div className="bg-amber-500 px-4 py-2 text-center text-sm text-white">
        ออฟไลน์อยู่ — คำตอบถูกเก็บไว้ในเครื่องและจะส่งอัตโนมัติเมื่อสัญญาณกลับมา
        {sync.pending > 0 && ` (ค้างอยู่ ${sync.pending} รายการ)`}
      </div>
    )
  }

  if (sync.failed > 0) {
    return (
      <div className="flex flex-wrap items-center justify-center gap-2 bg-red-50 px-4 py-2 text-center text-sm text-red-700">
        <span>ส่งไม่สำเร็จถาวร {sync.failed} รายการ (ข้อมูลยังอยู่ในเครื่อง ไม่หาย)</span>
        <button
          type="button"
          onClick={retryFailed}
          className="rounded-md border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
        >
          ลองส่งอีกครั้ง
        </button>
      </div>
    )
  }

  if (sync.lastError) {
    return <div className="bg-red-50 px-4 py-2 text-center text-sm text-red-700">{sync.lastError}</div>
  }

  if (sync.pending > 0) {
    return (
      <div className="bg-slate-100 px-4 py-1.5 text-center text-xs text-slate-600">
        กำลังส่งข้อมูล {sync.pending} รายการ...
      </div>
    )
  }

  return null
}

function FinishedScreen({
  message,
  pending,
  failed,
}: {
  message: string
  pending: number
  failed: number
}) {
  return (
    <div className="flex flex-col items-center gap-4 py-12 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-3xl">
        ✓
      </div>
      <h2 className="text-xl font-semibold text-slate-900">เสร็จสิ้น</h2>
      <p className="max-w-sm leading-relaxed text-slate-600">{message}</p>
      {failed > 0 && (
        <div className="w-full max-w-sm space-y-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <p>ส่งไม่สำเร็จถาวร {failed} รายการ (ข้อมูลยังอยู่ในเครื่อง ไม่หาย)</p>
          <button
            type="button"
            onClick={retryFailed}
            className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-red-100"
          >
            ลองส่งอีกครั้ง
          </button>
        </div>
      )}
      {pending > 0 ? (
        <Alert variant="warning">
          ยังมี {pending} รายการรอส่ง — เปิดแอปค้างไว้จนกว่าจะส่งครบ ข้อมูลจะไม่หาย
        </Alert>
      ) : failed === 0 ? (
        <Alert variant="success">ส่งข้อมูลขึ้นระบบเรียบร้อยแล้ว</Alert>
      ) : null}
      <Link
        to="/run"
        className="mt-2 inline-flex min-h-11 items-center rounded-lg bg-[var(--fs-primary)] px-5 py-2.5 text-sm font-medium text-[var(--fs-on-primary)]"
      >
        กลับไปรายการแบบสอบถาม
      </Link>
    </div>
  )
}
